import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/auth-provider";
import { getUserStorage } from "@/lib/user-storage";
import type { ColorPalette } from "@/lib/roster/constants";

export type RosterColorMode = "color" | "mono";

export interface PersonalizationSettings {
  hue: number;           // 0-360
  saturation: number;    // 0-100
  rosterColorMode: RosterColorMode;
  colorPalette: ColorPalette;
}

interface PersonalizationContextType {
  settings: PersonalizationSettings;
  updateSettings: (updates: Partial<PersonalizationSettings>) => void;
  resetToDefaults: () => void;
  isLoading: boolean;
}

// Default settings - ShiftCanvas teal
const DEFAULT_SETTINGS: PersonalizationSettings = {
  hue: 173,        // Teal
  saturation: 65,
  rosterColorMode: "color",
  colorPalette: "rainbow",
};

const PersonalizationContext = createContext<PersonalizationContextType | undefined>(undefined);

function parseSettings(stored: string | null): PersonalizationSettings {
  if (!stored) return DEFAULT_SETTINGS;
  try {
    const parsed = JSON.parse(stored);
    // Migrate old day/night values to mono
    let colorMode = parsed.rosterColorMode;
    if (colorMode === "day" || colorMode === "night") {
      colorMode = "mono";
    }
    return {
      hue: typeof parsed.hue === "number" ? Math.max(0, Math.min(360, parsed.hue)) : DEFAULT_SETTINGS.hue,
      saturation: typeof parsed.saturation === "number" ? Math.max(0, Math.min(100, parsed.saturation)) : DEFAULT_SETTINGS.saturation,
      rosterColorMode: ["color", "mono"].includes(colorMode) ? colorMode : DEFAULT_SETTINGS.rosterColorMode,
      colorPalette: ["rainbow", "ocean", "sunset", "forest", "mono", "pastel"].includes(parsed.colorPalette) ? parsed.colorPalette : DEFAULT_SETTINGS.colorPalette,
    };
  } catch (e) {
    console.warn("[Personalization] Failed to parse settings:", e);
    return DEFAULT_SETTINGS;
  }
}

// CSS variable mapping for primary color overrides
// Maps variable name to [lightModeLightness, darkModeLightness]
const PRIMARY_COLOR_VARS: Record<string, [number, number]> = {
  "--primary": [45, 45],
  "--ring": [45, 45],
  "--sidebar-primary": [45, 45],
  "--sidebar-ring": [45, 45],
  "--chart-1": [45, 55],
};

export function applyPersonalization(hue: number, saturation: number, isDark: boolean): void {
  const root = document.documentElement;
  
  for (const [varName, [lightL, darkL]] of Object.entries(PRIMARY_COLOR_VARS)) {
    const lightness = isDark ? darkL : lightL;
    root.style.setProperty(varName, `${hue} ${saturation}% ${lightness}%`);
  }
}

export function clearPersonalization(): void {
  const root = document.documentElement;
  for (const varName of Object.keys(PRIMARY_COLOR_VARS)) {
    root.style.removeProperty(varName);
  }
}

function isAppRoute(path: string): boolean {
  return path.startsWith("/app");
}

export function PersonalizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const userStorage = useMemo(() => getUserStorage(userId), [userId]);
  
  const [settings, setSettings] = useState<PersonalizationSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [location] = useLocation();
  
  // Load settings when userId becomes available
  useEffect(() => {
    const stored = userStorage.getPersonalization();
    const loadedSettings = parseSettings(stored);
    setSettings(loadedSettings);
    setIsLoading(false);
  }, [userId, userStorage]);

  // Apply personalization only on app routes
  useEffect(() => {
    if (isAppRoute(location)) {
      const isDark = document.documentElement.classList.contains("dark");
      applyPersonalization(settings.hue, settings.saturation, isDark);
    } else {
      clearPersonalization();
    }
  }, [settings.hue, settings.saturation, location]);

  // Re-apply when theme or brand changes (only on app routes)
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class" || mutation.attributeName === "data-brand") {
          if (isAppRoute(location)) {
            const isDark = document.documentElement.classList.contains("dark");
            applyPersonalization(settings.hue, settings.saturation, isDark);
          } else {
            clearPersonalization();
          }
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, [settings.hue, settings.saturation, location]);

  const updateSettings = useCallback((updates: Partial<PersonalizationSettings>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...updates };
      userStorage.setPersonalization(JSON.stringify(newSettings));
      return newSettings;
    });
  }, [userStorage]);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    userStorage.setPersonalization(JSON.stringify(DEFAULT_SETTINGS));
  }, [userStorage]);

  return (
    <PersonalizationContext.Provider value={{ settings, updateSettings, resetToDefaults, isLoading }}>
      {children}
    </PersonalizationContext.Provider>
  );
}

export function usePersonalization() {
  const context = useContext(PersonalizationContext);
  if (context === undefined) {
    throw new Error("usePersonalization must be used within a PersonalizationProvider");
  }
  return context;
}

export function usePersonalizationSafe() {
  const context = useContext(PersonalizationContext);
  return context || {
    settings: DEFAULT_SETTINGS,
    updateSettings: () => {},
    resetToDefaults: () => {},
    isLoading: false,
  };
}

export { DEFAULT_SETTINGS };
